import Button from "components/Button/Button";
import { KaboodleToaster } from "components/common";
import useQuickBooksConnectionStatus from "components/Invoicing/QuickBooks/useQuickBooksConnectionStatus";
import useQuickBooksReconnect from "components/Invoicing/QuickBooks/useQuickBooksReconnect";
import { Tooltip } from "components/Tooltip/Tooltip";
import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useReportActions } from "../contexts/ReportActionsContext";
import { getRowId } from "../utils/reportUtils";
import Section111InvoiceModal from "./Section111InvoiceModal";

type Section111InvoiceActionsProps = {
  selectedRows: Set<string | number>;
  reportData: Record<string, unknown>[];
  reportMeta: Record<string, unknown> | null;
  totalRecords: number;
  onClearSelection: () => void;
};

const Section111InvoiceActions: React.FC<Section111InvoiceActionsProps> = ({
  selectedRows,
  reportData,
  reportMeta,
  totalRecords,
  onClearSelection,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [searchParams] = useSearchParams();
  const { state } = useReportActions();
  const isSelectAllMode = state.isSelectAllMode;
  const setIsSelectAllMode = state.setIsSelectAllMode;

  const {
    isConnectionValid,
    isLoading: isConnectionLoading,
    connectionMessage,
  } = useQuickBooksConnectionStatus();
  const { startReconnection } = useQuickBooksReconnect();

  const rreId = searchParams.get("rreId")
    ? parseInt(searchParams.get("rreId")!, 10)
    : 0;
  const fileTypeParam = searchParams.get("fileType") as "C" | "Q" | null;

  const selectedDetails = Array.from(selectedRows)
    .map((rowId) => reportData.find((row) => getRowId(row) === rowId))
    .filter((row): row is Record<string, unknown> => row !== undefined);

  // "Detail IDs" is a comma-separated string of IDs per row (grouped by claim)
  const detailIds = selectedDetails
    .flatMap((row) => {
      const ids = String(row["Detail IDs"] || "");
      return ids.split(",").map((id) => Number(id.trim()));
    })
    .filter((id) => !isNaN(id) && id > 0);

  // Per-row flag only covers the currently-visible page, so in select-all
  // mode we use the report's meta flag (computed over the full filter set).
  const hasAlreadyInvoiced = isSelectAllMode
    ? Boolean(reportMeta?.has_already_invoiced)
    : selectedDetails.some(
        (row) =>
          String(
            row["Previously Invoiced"] || row.Invoiced || "",
          ).toLowerCase() === "yes",
      );

  const buildSelectAllFilters = useCallback(() => {
    const dateFrom = searchParams.get("sendDateFrom");
    const dateTo = searchParams.get("sendDateTo");
    const hideInvoiced = searchParams.get("hideInvoiced");
    const nsaFilter = searchParams.get("nsaFilter");
    const actionFilter = searchParams.get("actionFilter");
    const doiFrom = searchParams.get("doiFrom");
    const doiTo = searchParams.get("doiTo");
    const tpocFrom = searchParams.get("tpocFrom");
    const tpocTo = searchParams.get("tpocTo");
    const medicareFrom = searchParams.get("medicareFrom");
    const medicareTo = searchParams.get("medicareTo");

    const filters: {
      expected_count: number;
      date_from?: string;
      date_to?: string;
      hide_invoiced?: boolean;
      nsa_filter?: string;
      action_filter?: string;
      doi_from?: string;
      doi_to?: string;
      tpoc_from?: string;
      tpoc_to?: string;
      medicare_from?: string;
      medicare_to?: string;
    } = {
      expected_count: totalRecords,
      hide_invoiced: hideInvoiced !== "false",
    };

    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    if (nsaFilter) filters.nsa_filter = nsaFilter;
    if (actionFilter) filters.action_filter = actionFilter;
    if (doiFrom) filters.doi_from = doiFrom;
    if (doiTo) filters.doi_to = doiTo;
    if (tpocFrom) filters.tpoc_from = tpocFrom;
    if (tpocTo) filters.tpoc_to = tpocTo;
    if (medicareFrom) filters.medicare_from = medicareFrom;
    if (medicareTo) filters.medicare_to = medicareTo;

    return filters;
  }, [searchParams, totalRecords]);

  const displayCount = isSelectAllMode ? totalRecords : selectedRows.size;

  const handleCreateClick = () => {
    if (!isConnectionValid) {
      KaboodleToaster.error(
        "QuickBooks connection is required to create invoices",
      );
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmProceed = () => {
    setShowConfirmation(false);
    setIsModalOpen(true);
  };

  return (
    <>
      {/* QB connection state → Create Invoice button */}
      {isConnectionLoading ? (
        <Button className="report-button" onClick={() => {}} disabled>
          <span className="material-icons">hourglass_empty</span>
          Connecting...
        </Button>
      ) : !isConnectionValid ? (
        <Tooltip
          content={
            connectionMessage || "Connect to QuickBooks to create invoices"
          }
          variant="info"
          className="batch-invoice-tooltip-container"
        >
          <Button className="report-button" onClick={startReconnection}>
            <span className="material-icons">refresh</span>
            Reconnect QuickBooks
          </Button>
        </Tooltip>
      ) : (
        <Tooltip
          content={
            displayCount === 0
              ? "Select details to create invoice"
              : `Create invoice for ${displayCount.toLocaleString()} detail${displayCount === 1 ? "" : "s"}`
          }
          variant="info"
          className="batch-invoice-tooltip-container"
        >
          <Button
            type="button"
            onClick={handleCreateClick}
            disabled={displayCount === 0 || !fileTypeParam}
            className="report-button success"
          >
            <span className="material-icons">receipt_long</span>
            {displayCount > 0
              ? `Create Invoice (${displayCount.toLocaleString()})`
              : "Create Invoice"}
          </Button>
        </Tooltip>
      )}

      {/* Confirmation dialog */}
      {showConfirmation && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowConfirmation(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "480px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Create Section 111 Invoice
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                You are about to create invoices for:
              </p>
              <ul
                style={{
                  margin: "0 0 8px",
                  paddingLeft: "20px",
                }}
              >
                <li>
                  <strong>{displayCount.toLocaleString()}</strong>{" "}
                  {fileTypeParam === "C" ? "claim" : "query"} detail
                  {displayCount === 1 ? "" : "s"}
                </li>
                {isSelectAllMode && (
                  <li>
                    Using <strong>all results</strong> matching current filters
                    (across all pages)
                  </li>
                )}
              </ul>
              {hasAlreadyInvoiced && (
                <p
                  style={{
                    margin: "8px 0 0",
                    color: "#b45309",
                    fontSize: "13px",
                  }}
                >
                  Some selected details have already been invoiced.
                </p>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => setShowConfirmation(false)}>Cancel</Button>
              <Button className="success" onClick={handleConfirmProceed}>
                Continue to Invoice Details
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice creation modal */}
      {isModalOpen && fileTypeParam && (
        <Section111InvoiceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          detailIds={isSelectAllMode ? undefined : detailIds}
          selectAllFilters={
            isSelectAllMode ? buildSelectAllFilters() : undefined
          }
          totalCount={displayCount}
          fileType={fileTypeParam}
          rreId={rreId}
          hasAlreadyInvoiced={hasAlreadyInvoiced}
          onSuccess={() => {
            setIsModalOpen(false);
            setIsSelectAllMode?.(false);
            onClearSelection();
            state.onViewReport?.();
          }}
        />
      )}
    </>
  );
};

export default Section111InvoiceActions;
